/**
 * Looping Strategy
 */
import { Decimal } from 'decimal.js';
import { BaseStrategy } from './strategy.js';
import { normalizeStructTag } from '@mysten/sui/utils';
import { ALPHALEND_LENDING_PROTOCOL_ID, CLOCK_PACKAGE_ID, DISTRIBUTOR_OBJECT_ID, GLOBAL_CONFIGS, NAVI_CONFIG, POOLS, STSUI, SUI_SYSTEM_STATE, VERSIONS, } from '../utils/constants.js';
import { stSuiExchangeRate, getConf as getStSuiConf } from '@alphafi/stsui-sdk';
import { getConfig, getPriceFeeds, getUserAvailableLendingRewards, updateOraclePricesPTB, updatePythPriceFeeds, } from '@naviprotocol/lending';
/**
 * Looping Strategy for leveraged positions with automated compounding
 */
export class LoopingStrategy extends BaseStrategy {
    poolLabel;
    poolObject;
    investorObject;
    receiptObjects = [];
    context;
    constructor(poolLabel, poolObject, investorObject, context) {
        super();
        this.poolLabel = poolLabel;
        this.poolObject = this.parsePoolObject(poolObject);
        this.investorObject = this.parseInvestorObject(investorObject);
        this.context = context;
    }
    getPoolLabel() {
        return this.poolLabel;
    }
    getOtherAmount(_amount, _isAmountA) {
        throw new Error('getOtherAmount is not supported for single-asset Looping strategy');
    }
    updateReceipts(receipts) {
        this.receiptObjects = this.parseReceiptObjects(receipts);
    }
    /**
     * Get alpha mining data including pool and receipt information
     */
    getAlphaMiningData() {
        const receipt = this.receiptObjects.length > 0 ? this.receiptObjects[0] : null;
        return {
            poolId: this.poolLabel.poolId,
            accRewardsPerXtoken: this.poolObject.accRewardsPerXtoken,
            xTokenSupply: this.poolObject.xTokenSupply,
            receipt: receipt
                ? {
                    lastAccRewardPerXtoken: receipt.lastAccRewardPerXtoken,
                    pendingRewards: receipt.pendingRewards,
                    xTokenBalance: receipt.xTokenBalance,
                }
                : null,
        };
    }
    /**
     * Get the exchange rate for xtoken to underlying token ratio
     * Adjusted for current debt-to-supply ratio in leveraged positions
     */
    exchangeRate() {
        const currentDebtToSupplyRatio = new Decimal(this.investorObject.currentDebtToSupplyRatio);
        const tokensInvested = new Decimal(this.investorObject.tokensDeposited).mul(new Decimal(1).minus(currentDebtToSupplyRatio.div(new Decimal(10).pow(20))));
        const xtokenSupply = new Decimal(this.poolObject.xTokenSupply);
        if (xtokenSupply.isZero()) {
            return new Decimal(1); // Default exchange rate when no tokens are supplied
        }
        return tokensInvested.div(xtokenSupply);
    }
    /**
     * Get comprehensive pool data including TVL and APR information
     */
    async getData() {
        const [alphafi, parent, apr] = await Promise.all([
            this.getTvl(),
            this.getParentTvl(),
            this.context.getAprData(this.poolLabel.poolId),
        ]);
        return {
            poolId: this.poolLabel.poolId,
            strategyType: this.poolLabel.strategyType,
            coinType: this.poolLabel.userDepositAsset.type,
            poolName: this.poolLabel.poolName,
            apr,
            tvl: {
                alphafi,
                parent,
            },
        };
    }
    /**
     * Calculate total value locked adjusted for leveraged position
     * Accounts for debt-to-supply ratio and protocol-specific scaling
     */
    async getTvl() {
        const supplyType = this.poolLabel.supplyAsset.type;
        const userDepositType = this.poolLabel.userDepositAsset.type;
        const cdsr = new Decimal(this.investorObject.currentDebtToSupplyRatio);
        let tokensInvested = new Decimal(this.poolObject.tokensInvested).mul(new Decimal(1).minus(cdsr.div(new Decimal(10).pow(20))));
        if (this.poolLabel.parentProtocol === 'Navi') {
            tokensInvested = tokensInvested.div(new Decimal(10).pow(9));
        }
        else {
            const supplyDecimals = (await this.context.coinInfoProvider.getCoinByType(supplyType))?.decimals ?? 9;
            tokensInvested = tokensInvested.div(new Decimal(10).pow(supplyDecimals));
        }
        const supplyPrice = await this.context.getCoinPrice(supplyType);
        const userDepositPrice = await this.context.getCoinPrice(userDepositType);
        const tokenAmount = tokensInvested.mul(supplyPrice).div(userDepositPrice);
        const usdValue = tokensInvested.mul(userDepositPrice);
        return { tokenAmount, usdValue };
    }
    /**
     * Calculate parent protocol TVL based on protocol type (Navi/Alphalend)
     */
    async getParentTvl() {
        const protocol = this.poolLabel.parentProtocol;
        const coinType = this.poolLabel.supplyAsset.type;
        const price = await this.context.getCoinPrice(coinType);
        if (protocol === 'Navi') {
            const tokenAmountUsd = await this.context.getNaviTvlByPoolId(this.poolLabel.poolId);
            return { tokenAmount: tokenAmountUsd.div(price), usdValue: tokenAmountUsd };
        }
        else if (protocol === 'Alphalend') {
            const tokenAmount = await this.context.getAlphaLendTvl(coinType);
            return { tokenAmount, usdValue: tokenAmount.mul(price) };
        }
        throw new Error(`Unsupported parent protocol: ${protocol}`);
    }
    /**
     * Calculate user's current pool balance from xToken balance
     * Includes leverage adjustment and protocol-specific scaling
     */
    async getBalance(_userAddress) {
        if (this.receiptObjects.length === 0 || this.receiptObjects[0].xTokenBalance === '0') {
            return { tokenAmount: new Decimal(0), usdValue: new Decimal(0) };
        }
        const xTokens = new Decimal(this.receiptObjects[0].xTokenBalance);
        const exchangeRate = this.exchangeRate();
        let tokens = xTokens.mul(exchangeRate);
        if (this.poolLabel.parentProtocol === 'Navi') {
            tokens = tokens.div(new Decimal(10).pow(9));
        }
        else {
            const supplyDecimals = await this.context.getCoinDecimals(this.poolLabel.supplyAsset.type);
            tokens = tokens.div(new Decimal(10).pow(supplyDecimals));
        }
        const [supplyPrice, userDepositPrice] = await Promise.all([
            this.context.getCoinPrice(this.poolLabel.supplyAsset.type),
            this.context.getCoinPrice(this.poolLabel.userDepositAsset.type),
        ]);
        const amount = tokens.mul(supplyPrice).div(userDepositPrice);
        return { tokenAmount: amount, usdValue: amount.mul(userDepositPrice) };
    }
    /**
     * Parse pool object from blockchain response
     */
    parsePoolObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                accRewardsPerXtoken: this.parseVecMap(fields.acc_rewards_per_xtoken || {}),
                depositFee: this.getStringField(fields, 'deposit_fee'),
                depositFeeMaxCap: this.getStringField(fields, 'deposit_fee_max_cap'),
                id: this.getStringField(fields, 'id'),
                imageUrl: this.getStringField(fields, 'image_url'),
                name: this.getStringField(fields, 'name'),
                paused: this.getBooleanField(fields, 'paused', false),
                rewards: (() => {
                    const idVal = this.getNestedField(fields, 'rewards.id');
                    const sizeVal = this.getNestedField(fields, 'rewards.size');
                    return { id: String(idVal), size: String(sizeVal) };
                })(),
                tokensInvested: this.getStringField(fields, 'tokensInvested'),
                withdrawFeeMaxCap: this.getStringField(fields, 'withdraw_fee_max_cap'),
                withdrawalFee: this.getStringField(fields, 'withdrawal_fee'),
                xTokenSupply: this.getStringField(fields, 'xTokenSupply'),
            };
        }, 'Failed to parse Looping pool object');
    }
    /**
     * Parse investor object from blockchain response
     */
    parseInvestorObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                currentDebtToSupplyRatio: this.getStringField(fields, 'current_debt_to_supply_ratio'),
                freeRewards: (() => {
                    const idVal = this.getNestedField(fields, 'free_rewards.id');
                    const sizeVal = this.getNestedField(fields, 'free_rewards.size');
                    return { id: String(idVal), size: String(sizeVal) };
                })(),
                id: this.getStringField(fields, 'id'),
                loops: this.getStringField(fields, 'loops'),
                maxCapPerformanceFee: this.getStringField(fields, 'max_cap_performance_fee'),
                minimumSwapAmount: this.getStringField(fields, 'minimum_swap_amount'),
                naviAccCap: {
                    id: this.getNestedField(fields, 'navi_acc_cap.id'),
                    owner: this.getNestedField(fields, 'navi_acc_cap.owner'),
                },
                performanceFee: this.getStringField(fields, 'performance_fee'),
                safeBorrowPercentage: this.getStringField(fields, 'safe_borrow_percentage'),
                tokensDeposited: this.getStringField(fields, 'tokens_deposited') ||
                    this.getStringField(fields, 'tokensDeposited'),
            };
        }, 'Failed to parse Looping investor object');
    }
    /**
     * Parse parent pool object (not applicable for Looping)
     */
    parseParentPoolObject(_response) {
        throw new Error('Looping strategy does not have parent pool objects');
    }
    /**
     * Parse receipt objects from blockchain responses
     */
    parseReceiptObjects(responses) {
        return responses
            .map((response, index) => {
            return this.safeParseObject(() => {
                const fields = this.extractFields(response);
                return {
                    id: this.getStringField(fields, 'id'),
                    imageUrl: this.getStringField(fields, 'image_url'),
                    lastAccRewardPerXtoken: this.parseVecMap(fields.last_acc_reward_per_xtoken || {}),
                    name: this.getStringField(fields, 'name'),
                    owner: this.getStringField(fields, 'owner'),
                    pendingRewards: this.parseVecMap(fields.pending_rewards || {}),
                    poolId: this.getStringField(fields, 'pool_id'),
                    xTokenBalance: this.getStringField(fields, 'xTokenBalance'),
                };
            }, `Failed to parse Looping receipt object at index ${index}`);
        })
            .filter((receipt) => receipt.poolId === this.poolLabel.poolId);
    }
    async getAvailableRewards(address) {
        try {
            const rewards = await getUserAvailableLendingRewards(address, {
                client: this.context.blockchain.suiGrpcClient,
            });
            // Group by asset coin type (same shape the integration API returned).
            const rewardsByAsset = {};
            for (const reward of rewards) {
                if (!reward.assetCoinType)
                    continue;
                // Normalize the group key so it matches the normalizeStructTag(...) lookups below;
                // otherwise a NAVI format change silently drops rewards.
                const assetKey = normalizeStructTag(reward.assetCoinType);
                if (!rewardsByAsset[assetKey])
                    rewardsByAsset[assetKey] = [];
                rewardsByAsset[assetKey].push(reward);
            }
            return rewardsByAsset;
        }
        catch (error) {
            console.error('Error fetching Navi rewards:', error);
            throw new Error(`Failed to fetch Navi rewards: ${error.message}`);
        }
    }
    async collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards) {
        if (!claimableRewards) {
            return;
        }
        const [navxCoin, vsuiCoin, stsuiCoin, suiCoin] = await this.context.getCoinsBySymbols([
            'NAVX',
            'vSUI',
            'stSUI',
            'SUI',
        ]);
        for (const reward of claimableRewards) {
            if (rewardCoinSet.has(reward.rewardCoinType) === false) {
                if (this.poolLabel.supplyAsset.name === 'vSUI') {
                    if (reward.rewardCoinType === navxCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_sui_vsui_investor::collect_reward_with_two_swaps_v2`,
                            typeArguments: [navxCoin.coinType],
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.NAVX),
                                tx.object(NAVI_CONFIG.VOLO.STAKE_POOL),
                                tx.object(NAVI_CONFIG.VOLO.METADATA),
                                tx.object(SUI_SYSTEM_STATE),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('NAVX', 'vSUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                            ],
                        });
                    }
                    else if (reward.rewardCoinType === vsuiCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_sui_vsui_investor::collect_reward_with_no_swap`,
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.vSUI),
                            ],
                        });
                    }
                }
                else if (this.poolLabel.supplyAsset.name === 'HASUI') {
                    if (reward.rewardCoinType === navxCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_hasui_sui_investor::collect_reward_with_two_swaps`,
                            typeArguments: [navxCoin.coinType],
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.NAVX),
                                tx.object(NAVI_CONFIG.HAEDEL_STAKING),
                                tx.object(SUI_SYSTEM_STATE),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('NAVX', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('HASUI', 'SUI', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                            ],
                        });
                    }
                    else if (reward.rewardCoinType === vsuiCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_hasui_sui_investor::collect_reward_with_two_swaps`,
                            typeArguments: [vsuiCoin.coinType],
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.vSUI),
                                tx.object(NAVI_CONFIG.HAEDEL_STAKING),
                                tx.object(SUI_SYSTEM_STATE),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('HASUI', 'SUI', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                            ],
                        });
                    }
                }
                else if (this.poolLabel.supplyAsset.name === 'stSUI') {
                    if (reward.rewardCoinType === navxCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::collect_v3_rewards_with_two_swaps_bluefin`,
                            typeArguments: [navxCoin.coinType],
                            arguments: [
                                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                                tx.object(this.poolLabel.investorId),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.NAVX),
                                tx.object(STSUI.LST_INFO),
                                tx.object(SUI_SYSTEM_STATE),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('NAVX', 'SUI', 'bluefin')),
                                tx.object(GLOBAL_CONFIGS.BLUEFIN),
                                tx.object(CLOCK_PACKAGE_ID),
                            ],
                        });
                    }
                    else if (reward.rewardCoinType === vsuiCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::collect_v3_rewards_with_two_swaps`,
                            typeArguments: [vsuiCoin.coinType],
                            arguments: [
                                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                                tx.object(this.poolLabel.investorId),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.vSUI),
                                tx.object(STSUI.LST_INFO),
                                tx.object(SUI_SYSTEM_STATE),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                                tx.object(CLOCK_PACKAGE_ID),
                            ],
                        });
                    }
                    else if (reward.rewardCoinType === stsuiCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::collect_v3_rewards_with_no_swap`,
                            arguments: [
                                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                                tx.object(this.poolLabel.investorId),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.stSUI),
                                tx.object(CLOCK_PACKAGE_ID),
                            ],
                        });
                    }
                }
                else if (this.poolLabel.supplyAsset.name === 'USDC') {
                    if (reward.rewardCoinType === navxCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_native_usdc_usdt_investor::collect_reward_with_two_swaps`,
                            typeArguments: [navxCoin.coinType],
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.NAVX),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('NAVX', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'SUI', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                            ],
                        });
                    }
                    else if (reward.rewardCoinType === vsuiCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_native_usdc_usdt_investor::collect_reward_with_two_swaps`,
                            typeArguments: [vsuiCoin.coinType],
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.vSUI),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'SUI', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                            ],
                        });
                    }
                }
                else if (this.poolLabel.supplyAsset.name === 'USDT') {
                    // Rewards route reward -> SUI -> USDC -> USDT; `update_pool_v3` asserts the
                    // investor has no unclaimed incentive-v3 rewards, so skipping this aborts 15151.
                    if (reward.rewardCoinType === vsuiCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_usdt_usdc_investor::collect_v3_rewards_with_three_swaps`,
                            typeArguments: [
                                this.poolLabel.supplyAsset.type,
                                this.poolLabel.borrowAsset.type,
                                suiCoin.coinType,
                                vsuiCoin.coinType,
                            ],
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.vSUI),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'USDT', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                            ],
                        });
                    }
                    else if (reward.rewardCoinType === navxCoin.coinType) {
                        tx.moveCall({
                            target: `${this.poolLabel.packageId}::alphafi_navi_usdt_usdc_investor::collect_v3_rewards_with_three_swaps`,
                            typeArguments: [
                                this.poolLabel.supplyAsset.type,
                                this.poolLabel.borrowAsset.type,
                                suiCoin.coinType,
                                navxCoin.coinType,
                            ],
                            arguments: [
                                tx.object(this.poolLabel.investorId),
                                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                                tx.object(CLOCK_PACKAGE_ID),
                                tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                                tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                                tx.object(NAVI_CONFIG.REWARDS_POOL.NAVX),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('NAVX', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'SUI', 'cetus')),
                                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'USDT', 'cetus')),
                                tx.object(GLOBAL_CONFIGS.CETUS),
                            ],
                        });
                    }
                }
                rewardCoinSet.add(reward.rewardCoinType);
            }
        }
    }
    async collectAlphalendSuiStsuiRewards(tx) {
        const [alphaCoin, blueCoin] = await this.context.getCoinsBySymbols(['ALPHA', 'BLUE']);
        tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::collect_v3_rewards_with_one_swap`,
            typeArguments: [alphaCoin.coinType],
            arguments: [
                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                tx.object(this.poolLabel.investorId),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('ALPHA', 'STSUI', 'bluefin')),
                tx.object(GLOBAL_CONFIGS.BLUEFIN),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
        tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::collect_v3_rewards_with_no_swap_v2`,
            arguments: [
                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                tx.object(this.poolLabel.investorId),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
        tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::collect_v3_rewards_with_two_swaps_v2`,
            typeArguments: [blueCoin.coinType],
            arguments: [
                tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                tx.object(this.poolLabel.investorId),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(STSUI.LST_INFO),
                tx.object(SUI_SYSTEM_STATE),
                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('BLUE', 'SUI', 'cetus')),
                // `collect_v3_rewards_with_two_swaps_v2` is the Cetus variant, so the Cetus
                // BLUE/SUI pool must be paired with the Cetus global config. The withdraw and
                // updatePool paths already used GLOBAL_CONFIGS.CETUS here; the old inline deposit
                // copy incorrectly used GLOBAL_CONFIGS.BLUEFIN with this Cetus pool (which would
                // abort on-chain). Unifying on CETUS in this shared helper fixes that deposit bug.
                tx.object(GLOBAL_CONFIGS.CETUS),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
    }
    async collectAndSwapRewards(tx) {
        const rewardCoinSet = new Set();
        if (this.poolLabel.supplyAsset.name === 'vSUI') {
            const claimableRewards = await this.getAvailableRewards(NAVI_CONFIG.ACCOUNT_ADDRESSES.SUI_VSUI_LOOP);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag(this.poolLabel.supplyAsset.type)]);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag('0x2::sui::SUI')]);
        }
        else if (this.poolLabel.supplyAsset.name === 'HASUI') {
            const claimableRewards = await this.getAvailableRewards(NAVI_CONFIG.ACCOUNT_ADDRESSES.HASUI_SUI_LOOP);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag(this.poolLabel.supplyAsset.type)]);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag('0x2::sui::SUI')]);
        }
        else if (this.poolLabel.supplyAsset.name === 'stSUI') {
            const claimableRewards = await this.getAvailableRewards('');
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag(this.poolLabel.supplyAsset.type)]);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag('0x2::sui::SUI')]);
        }
        else if (this.poolLabel.supplyAsset.name === 'USDC') {
            const claimableRewards = await this.getAvailableRewards(NAVI_CONFIG.ACCOUNT_ADDRESSES.USDC_USDT_LOOP);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag(this.poolLabel.supplyAsset.type)]);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag(this.poolLabel.borrowAsset.type)]);
        }
        else if (this.poolLabel.supplyAsset.name === 'USDT') {
            const claimableRewards = await this.getAvailableRewards(NAVI_CONFIG.ACCOUNT_ADDRESSES.USDT_USDC_LOOP);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag(this.poolLabel.supplyAsset.type)]);
            await this.collectAndSwapRewardsTxb(tx, rewardCoinSet, claimableRewards[normalizeStructTag(this.poolLabel.borrowAsset.type)]);
        }
    }
    /**
     * Overrides this tx's NAVI `lending_core` linkage by calling the latest package directly.
     *
     * Our pool packages reach `lending_core` through linkage tables frozen at publish time. Sui
     * resolves one version per original package per tx, so this direct call pulls those transitive
     * calls forward. The package comes from NAVI's config — the same value their own SDK helpers
     * use — so it tracks their upgrades and can't drift or collide. `version_verification` asserts
     * `storage.version == constants::version()`, so a wrong version fails loudly.
     */
    async overrideNaviLendingCoreLinkage(tx) {
        const { package: lendingCore } = await getConfig();
        tx.moveCall({
            target: `${lendingCore}::storage::version_verification`,
            arguments: [tx.object(NAVI_CONFIG.NAVI_STORAGE_ID)],
        });
    }
    async updateSingleTokenPrice(tx, feedId) {
        const feed = (await getPriceFeeds()).find((f) => f.feedId === feedId);
        if (!feed) {
            throw new Error(`NAVI oracle config has no price feed for feedId ${feedId}`);
        }
        // NAVI treats both as optional; a supra/switchboard-only feed would otherwise surface as
        // an opaque Hermes error or `tx.object('')` inside updateOraclePricesPTB.
        if (!feed.pythPriceFeedId || !feed.pythPriceInfoObject) {
            throw new Error(`NAVI price feed ${feedId} has no Pyth feed configured`);
        }
        await this.overrideNaviLendingCoreLinkage(tx);
        // Pass our client so the SuiPythClient object reads use the configured endpoint rather
        // than NAVI's module-level default (the public mainnet fullnode).
        const naviOptions = { client: this.context.blockchain.suiGrpcClient };
        // Post unconditionally: updateOraclePricesPTB's own flag gates on a stale check that
        // misparses the on-chain price struct, so it never posts.
        await updatePythPriceFeeds(tx, [feed.pythPriceFeedId], naviOptions);
        await updateOraclePricesPTB(tx, [feed], naviOptions);
    }
    async deposit(tx, options) {
        // get Coin Object
        const depositCoin = this.context.blockchain.getCoinObject(tx, this.poolLabel.userDepositAsset.type, options.address, options.amount);
        const receiptOption = this.context.blockchain.getOptionReceipt(tx, this.poolLabel.receipt.type, this.receiptObjects.length > 0 ? this.receiptObjects[0].id : undefined);
        if (this.poolLabel.parentProtocol === 'Alphalend') {
            const [stsuiCoin, suiCoin] = await this.context.getCoinsBySymbols(['stSUI', 'SUI']);
            const alphalendClient = this.context.alphalendClient;
            await alphalendClient.updatePrices(tx, [stsuiCoin.coinType, suiCoin.coinType]);
            await this.collectAlphalendSuiStsuiRewards(tx);
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::user_deposit_v3`,
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    receiptOption,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(STSUI.LST_INFO),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            return;
        }
        await this.updateSingleTokenPrice(tx, NAVI_CONFIG.PRICE_FEED[this.poolLabel.supplyAsset.name]
            .feedId);
        await this.updateSingleTokenPrice(tx, NAVI_CONFIG.PRICE_FEED[this.poolLabel.borrowAsset.name]
            .feedId);
        await this.collectAndSwapRewards(tx);
        if (this.poolLabel.supplyAsset.name === 'vSUI') {
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_sui_vsui_pool::user_deposit_v3`,
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    receiptOption,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.vSUI),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.SUI),
                    tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                    tx.object(NAVI_CONFIG.VOLO.STAKE_POOL),
                    tx.object(NAVI_CONFIG.VOLO.METADATA),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(NAVI_CONFIG.KRIYA.VSUI_SUI_POOL),
                    tx.object(NAVI_CONFIG.KRIYA.VERSION),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
        else if (this.poolLabel.supplyAsset.name === 'HASUI') {
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_hasui_sui_pool::user_deposit_v2`, // change package id for testing
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[2]), // change version object id for testing
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    receiptOption,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.HASUI),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.SUI),
                    tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('HASUI', 'SUI', 'cetus')),
                    tx.object(NAVI_CONFIG.HAEDEL_STAKING),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
        else if (this.poolLabel.supplyAsset.name === 'USDC') {
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_native_usdc_usdt_pool::user_deposit_v3`,
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    receiptOption,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDC),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDT),
                    tx.object(NAVI_CONFIG.INCENTIVE_V1_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(NAVI_CONFIG.FUNDS_POOLS.vSUI),
                    tx.object(NAVI_CONFIG.FUNDS_POOLS.NAVX),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('NAVX', 'SUI', 'cetus')),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'SUI', 'cetus')),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'USDT', 'cetus')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDT', 'USDC', 'bluefin')),
                    tx.pure.bool(true),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
        else if (this.poolLabel.supplyAsset.name === 'USDT') {
            const [navxCoin] = await this.context.getCoinsBySymbols(['NAVX']);
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_usdt_usdc_pool::user_deposit_v3`,
                typeArguments: [navxCoin.coinType],
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    receiptOption,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDT),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDC),
                    tx.object(NAVI_CONFIG.INCENTIVE_V1_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(NAVI_CONFIG.FUNDS_POOLS.vSUI),
                    tx.object(NAVI_CONFIG.FUNDS_POOLS.NAVX),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'USDT', 'cetus')),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'SUI', 'cetus')),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('NAVX', 'SUI', 'cetus')),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
    async fetchVoloExchangeRate() {
        const apiUrl = 'https://open-api.naviprotocol.io/api/volo/stats';
        const default_volo_data = {
            data: {
                operatorBalance: '',
                collectableFee: '',
                pendingStakes: '',
                poolTotalRewards: '0',
                unstakeTicketSupply: '',
                totalStaked: '',
                activeStake: '',
                calcTotalRewards: '',
                currentEpoch: '',
                validators: {},
                exchangeRate: (1 / 0.973).toString(),
                totalSupply: '',
                apy: '',
                sortedValidators: [''],
                maxInstantUnstake: '',
                maxNoFeeUnstake: '',
            },
            code: 0,
        };
        try {
            const response = await fetch(apiUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = (await response.json());
            return data;
        }
        catch (error) {
            console.error('error in api', error);
            return default_volo_data;
        }
    }
    async coinAmountToXToken(amount) {
        const exchangeRate = this.exchangeRate();
        if (this.poolLabel.poolName === 'NAVI-LOOP-SUI-VSUI') {
            const voloExchRate = await this.fetchVoloExchangeRate();
            amount = amount.div(parseFloat(voloExchRate.data.exchangeRate));
        }
        else if (this.poolLabel.poolName === 'ALPHALEND-LOOP-SUI-STSUI') {
            const suiTostSuiExchangeRate = await stSuiExchangeRate(getStSuiConf().LST_INFO, true);
            amount = amount.div(suiTostSuiExchangeRate);
        }
        return new Decimal(amount).div(exchangeRate).floor().toString();
    }
    async withdraw(tx, options) {
        if (this.receiptObjects.length === 0) {
            throw new Error('No receipt found');
        }
        let xtokenAmount = await this.coinAmountToXToken(new Decimal(options.amount));
        if (options.withdrawMax) {
            xtokenAmount = this.receiptObjects[0].xTokenBalance;
        }
        const noneAlphaReceipt = tx.moveCall({
            target: `0x1::option::none`,
            typeArguments: [
                '0x9bbd650b8442abb082c20f3bc95a9434a8d47b4bef98b0832dab57c1a8ba7123::alphapool::Receipt',
            ],
            arguments: [],
        });
        if (this.poolLabel.parentProtocol === 'Alphalend') {
            const [stsuiCoin, suiCoin] = await this.context.getCoinsBySymbols(['stSUI', 'SUI']);
            const alphalendClient = this.context.alphalendClient;
            await alphalendClient.updatePrices(tx, [stsuiCoin.coinType, suiCoin.coinType]);
            await this.collectAlphalendSuiStsuiRewards(tx);
            const [stsui_coin] = tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::user_withdraw_v3`,
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    tx.object(this.receiptObjects[0].id),
                    noneAlphaReceipt,
                    tx.object(POOLS.ALPHA_LEGACY),
                    tx.object(this.poolLabel.poolId),
                    tx.pure.u64(xtokenAmount),
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(STSUI.LST_INFO),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            this.context.blockchain.sendCoinToAddressBalance(tx, suiCoin.coinType, options.address, stsui_coin);
            return;
        }
        await this.updateSingleTokenPrice(tx, NAVI_CONFIG.PRICE_FEED[this.poolLabel.supplyAsset.name]
            .feedId);
        await this.updateSingleTokenPrice(tx, NAVI_CONFIG.PRICE_FEED[this.poolLabel.borrowAsset.name]
            .feedId);
        await this.collectAndSwapRewards(tx);
        if (this.poolLabel.supplyAsset.name === 'vSUI') {
            const [vsui_coin] = tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_sui_vsui_pool::user_withdraw_v3`,
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    tx.object(this.receiptObjects[0].id),
                    noneAlphaReceipt,
                    tx.object(POOLS.ALPHA_LEGACY),
                    tx.object(this.poolLabel.poolId),
                    tx.pure.u64(xtokenAmount),
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.vSUI),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.SUI),
                    tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('vSUI', 'SUI', 'cetus')),
                    tx.object(NAVI_CONFIG.VOLO.STAKE_POOL),
                    tx.object(NAVI_CONFIG.VOLO.METADATA),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(NAVI_CONFIG.KRIYA.VSUI_SUI_POOL),
                    tx.object(NAVI_CONFIG.KRIYA.VERSION),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            this.context.blockchain.sendCoinToAddressBalance(tx, this.poolLabel.supplyAsset.type, options.address, vsui_coin);
        }
        else if (this.poolLabel.supplyAsset.name === 'HASUI') {
            const [hasuiCoin] = tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_hasui_sui_pool::user_withdraw_v2`, // change package id for testing
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[2]), // change  version object id for testing
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    tx.object(this.receiptObjects[0].id),
                    noneAlphaReceipt,
                    tx.object(POOLS.ALPHA_LEGACY),
                    tx.object(this.poolLabel.poolId),
                    tx.pure.u64(xtokenAmount),
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.HASUI),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.SUI),
                    tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('HASUI', 'SUI', 'cetus')),
                    tx.object(NAVI_CONFIG.HAEDEL_STAKING),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            this.context.blockchain.sendCoinToAddressBalance(tx, this.poolLabel.supplyAsset.type, options.address, hasuiCoin);
        }
        else if (this.poolLabel.supplyAsset.name === 'USDC') {
            const [usdcCoin] = tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_native_usdc_usdt_pool::user_withdraw_v4`,
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[2]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    tx.object(this.receiptObjects[0].id),
                    noneAlphaReceipt,
                    tx.object(POOLS.ALPHA_LEGACY),
                    tx.object(this.poolLabel.poolId),
                    tx.pure.u256(xtokenAmount),
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDC),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDT),
                    tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'USDT', 'cetus')),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDT', 'USDC', 'bluefin')),
                    tx.pure.bool(true),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            this.context.blockchain.sendCoinToAddressBalance(tx, this.poolLabel.supplyAsset.type, options.address, usdcCoin);
        }
        else if (this.poolLabel.supplyAsset.name === 'USDT') {
            const [usdtCoin] = tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_navi_usdt_usdc_pool::user_withdraw_v3`,
                arguments: [
                    tx.object(VERSIONS.ALPHA_VERSIONS[5]),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    tx.object(this.receiptObjects[0].id),
                    noneAlphaReceipt,
                    tx.object(POOLS.ALPHA_LEGACY),
                    tx.object(this.poolLabel.poolId),
                    tx.pure.u256(xtokenAmount),
                    tx.object(this.poolLabel.investorId),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(NAVI_CONFIG.PRICE_ORACLE_ID),
                    tx.object(NAVI_CONFIG.NAVI_STORAGE_ID),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDT),
                    tx.object(NAVI_CONFIG.NAVI_POOLS.USDC),
                    tx.object(NAVI_CONFIG.INCENTIVE_V3_ID),
                    tx.object(NAVI_CONFIG.INCENTIVE_V2_ID),
                    tx.object(GLOBAL_CONFIGS.CETUS),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('USDC', 'USDT', 'cetus')),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            this.context.blockchain.sendCoinToAddressBalance(tx, this.poolLabel.supplyAsset.type, options.address, usdtCoin);
        }
    }
    async claimRewards(tx, alphaReceipt) {
        let target, version;
        if (this.poolLabel.poolName == 'NAVI-LOOP-USDT-USDC') {
            target = `${this.poolLabel.packageId}::alphafi_navi_usdt_usdc_pool::get_user_rewards_all`;
            version = VERSIONS.ALPHA_VERSIONS[5];
        }
        else if (this.poolLabel.poolName == 'ALPHALEND-LOOP-SUI-STSUI') {
            target = `${this.poolLabel.packageId}::alphafi_navi_sui_stsui_pool::get_user_rewards_all`;
            version = VERSIONS.ALPHA_VERSIONS[5];
        }
        else if (this.poolLabel.poolName == 'NAVI-LOOP-SUI-VSUI') {
            target = `${this.poolLabel.packageId}::alphafi_navi_sui_vsui_pool::get_user_rewards_all`;
            version = VERSIONS.ALPHA_VERSIONS[2];
        }
        else if (this.poolLabel.poolName == 'NAVI-LOOP-USDC-USDT') {
            target = `${this.poolLabel.packageId}::alphafi_navi_native_usdc_usdt_pool::get_user_rewards_all`;
            version = VERSIONS.ALPHA_VERSIONS[2];
        }
        else if (this.poolLabel.poolName == 'NAVI-LOOP-HASUI-SUI') {
            target = `${this.poolLabel.packageId}::alphafi_navi_hasui_sui_pool::get_user_rewards_all`;
            version = VERSIONS.ALPHA_VERSIONS[2];
        }
        if (version && target) {
            this.receiptObjects.forEach((receipt) => {
                alphaReceipt = tx.moveCall({
                    target,
                    arguments: [
                        tx.object(version),
                        tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                        tx.object(receipt.id),
                        alphaReceipt,
                        tx.object(this.poolLabel.poolId),
                        tx.object(POOLS.ALPHA_LEGACY),
                        tx.object(DISTRIBUTOR_OBJECT_ID),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
            });
        }
    }
}
//# sourceMappingURL=looping.js.map